import React, { useState, useEffect, useCallback } from 'react';
import { useApp, ROLE_OPTIONS, ROLE_LABELS } from '../context/AppContext';
import { InstructionGuide } from '../components/layout/InstructionGuide';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { UserPlus, Edit2, Trash2, Search, Filter, FileDown } from 'lucide-react';
import { toast } from 'sonner';

const emptyForm = { name: '', email: '', role: 'team_leader', phone: '', zone: '', manager_id: '', status: 'active' };

export default function TeamManagement() {
  const { api, userZone, roleLevel, currentUser } = useApp();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (roleFilter !== 'all') params.role = roleFilter;
      if (userZone) params.zone = userZone;
      const res = await api.getUsers(params);
      setUsers(res.data.data || res.data);
    } catch (e) { console.error(e); }
  }, [api, roleFilter, userZone]);

  useEffect(() => { load(); }, [load]);

  const filtered = users.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => { setEditingUser(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (u) => { setEditingUser(u); setForm({ name: u.name, email: u.email, role: u.role, phone: u.phone || '', zone: u.zone || '', manager_id: u.manager_id || '', status: u.status }); setDialogOpen(true); };

  const handleSubmit = async () => {
    if (!form.name || !form.email) { toast.error('Name and email are required'); return; }
    try {
      if (editingUser) {
        await api.updateUser(editingUser.id, form);
        toast.success('Team member updated');
      } else {
        await api.createUser(form);
        toast.success('Team member added');
      }
      setDialogOpen(false);
      load();
    } catch (e) { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this team member?')) return;
    try {
      await api.deleteUser(id);
      toast.success('Team member removed');
      load();
    } catch (e) { toast.error('Failed to delete'); }
  };

  return (
    <div data-testid="team-management-page" className="space-y-6 fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Team Management</h1>
          <p className="text-sm text-slate-500 mt-1">{users.length} team members</p>
        </div>
        <div className="flex items-center gap-2">
          <a href={api.exportTeam(userZone)} download data-testid="export-team-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
            <FileDown className="w-4 h-4" /> Export
          </a>
          <Button data-testid="add-team-member-btn" onClick={openCreate} className="gap-2 bg-amber-600 hover:bg-amber-700 text-white shadow-md">
            <UserPlus className="w-4 h-4" /> Add Member
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border border-border/60 shadow-sm">
        <CardContent className="p-4 flex items-center gap-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="team-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email..." className="pl-9" />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger data-testid="role-filter" className="w-48">
              <Filter className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Filter by role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {ROLE_OPTIONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border border-border/60 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 border-b border-slate-200">
                <TableHead className="text-slate-500 uppercase tracking-wider font-semibold text-xs">Name</TableHead>
                <TableHead className="text-slate-500 uppercase tracking-wider font-semibold text-xs">Role</TableHead>
                <TableHead className="text-slate-500 uppercase tracking-wider font-semibold text-xs">Zone</TableHead>
                <TableHead className="text-slate-500 uppercase tracking-wider font-semibold text-xs">Phone</TableHead>
                <TableHead className="text-slate-500 uppercase tracking-wider font-semibold text-xs">Status</TableHead>
                <TableHead className="text-slate-500 uppercase tracking-wider font-semibold text-xs text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(u => (
                <TableRow key={u.id} data-testid={`team-row-${u.id}`} className="border-b border-slate-100 hover:bg-slate-50/50 h-16">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-sm">
                        {u.name.charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{u.name}</p>
                        <p className="text-xs text-slate-400">{u.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell><Badge variant="secondary" className="text-xs">{ROLE_LABELS[u.role] || u.role}</Badge></TableCell>
                  <TableCell className="text-sm text-slate-600">{u.zone || '-'}</TableCell>
                  <TableCell className="text-sm text-slate-600">{u.phone || '-'}</TableCell>
                  <TableCell>
                    <Badge variant={u.status === 'active' ? 'default' : 'outline'} className={`text-xs ${u.status === 'active' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : ''}`}>
                      {u.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button data-testid={`edit-user-${u.id}`} variant="ghost" size="sm" onClick={() => openEdit(u)} className="hover:bg-amber-50 hover:text-amber-700"><Edit2 className="w-4 h-4" /></Button>
                      <Button data-testid={`delete-user-${u.id}`} variant="ghost" size="sm" onClick={() => handleDelete(u.id)} className="hover:bg-red-50 hover:text-red-600"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">No team members found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="team-member-dialog" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">{editingUser ? 'Edit' : 'Add'} Team Member</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2">
              <Label>Full Name</Label>
              <Input data-testid="member-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Enter full name" />
            </div>
            <div>
              <Label>Email</Label>
              <Input data-testid="member-email-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="email@company.com" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input data-testid="member-phone-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+91-XXXXXXXXXX" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="member-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Zone</Label>
              <Input data-testid="member-zone-input" value={form.zone} onChange={e => setForm({ ...form, zone: e.target.value })} placeholder="North, South, etc." />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="member-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid="save-member-btn" onClick={handleSubmit} className="bg-amber-600 hover:bg-amber-700 text-white">{editingUser ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
